import CustomList from '@/components/global/list/CustomList.jsx';
import { useMemo } from 'react';
import { SelectInput, FunctionField, usePermissions, useTranslate } from 'react-admin';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import { PromotionalCampaingsActions } from '@/components/promotionalCampaigns/PromotionalCampaingsActions.jsx';
import { PromotionalCampaignsStatisticList } from '@/components/analytics/statisticList.jsx';
import { Chip } from '@mui/material';
import { MarketingCampaignField } from '@/components/promotionalCampaigns/MarketingCampaignField.jsx';
import { MarketingPriorityField } from '@/components/promotionalCampaigns/MarketingPriorityField.jsx';
import { MarketingBarIndicatorField } from '@/components/promotionalCampaigns/MarketingBarIndicatorField.jsx';
import { PromotionalCampaingsMoreActionsPopover } from '@/components/promotionalCampaigns/PromotionalCampaingsMoreActionsPopover.jsx';
import { PromotionalCampaignsDetailsContent } from '@/components/promotionalCampaigns/PromotionalCampaignsDetailsContent.jsx';
import { Palette } from '@/themes/themeOverrides/Palette';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import CustomDatagridHeader from '@/components/global/list/CustomDatagridHeader.jsx';

export const PromotionalCampaigns = () => {
    const { isLoading } = usePermissions();
    const translate = useTranslate();

    const filters = useMemo(() => {
        // FOR TESTING. DELETE WHILE IMPLEMENTING REAL DATA
        const mockStatusChoices = [
            { id: 'Aktywna', name: 'Aktywna' },
            { id: 'Nieaktywna', name: 'Nieaktywna' },
            { id: 'Zakończona', name: 'Zakończona' },
        ];
        return [<SelectInput key='status' source='status' label={translate('common.status')} choices={mockStatusChoices} emptyText={'Wszystkie'} />];
    }, [translate]);

    if (isLoading) return null;

    return (
        <CustomList
            title={translate('menu.promotionalCampaigns')}
            component='div'
            disablePagination={true}
            filters={filters}
            actions={<PromotionalCampaingsActions />}
            sort={{ field: 'priority', order: 'ASC' }}
        >
            <>
                <PromotionalCampaignsStatisticList />
                <CustomDatagridConfigurable
                    expandSingle
                    rowClick='expand'
                    header={<CustomDatagridHeader />}
                    expand={record => <PromotionalCampaignsDetailsContent elementId={record.id} />}
                    bulkActionButtons={false}
                    sx={{
                        '& .RaDatagrid-table': {
                            minWidth: 800,
                            '& .MuiTableRow-root:hover': {
                                '& .MuiTableCell-root.column-name': {
                                    boxShadow: `1px 0 0 0 ${Palette.gray.gray50}, -2px 0 0 0 ${Palette.gray.gray300}, 0 -2px 0 0 ${Palette.gray.gray300}, 0 2px 0 0 ${Palette.gray.gray300}`,
                                },
                            },
                        },
                        '& .RaDatagrid-expandIconCell, & .RaDatagrid-headerCell:first-of-type': {
                            display: 'none',
                        },
                    }}
                >
                    <FunctionField label={translate('common.name')} source='name' render={record => <MarketingCampaignField record={record} />} />
                    <FunctionField
                        label={translate('resources.campaign.labels.schedule')}
                        source='startDate'
                        sortBy='startDate'
                        render={record => {
                            return `${record.startDate ? format(record.startDate, 'dd.MM', { locale: pl }) : '—'} - ${record.endDate ? format(record.endDate, 'dd.MM', { locale: pl }) : '—'}`;
                        }}
                        sx={{ whiteSpace: 'nowrap' }}
                    />
                    <FunctionField
                        source='status'
                        label={translate('common.status')}
                        render={record => (
                            <Chip
                                label={record.status}
                                size='small'
                                style={{ backgroundColor: record.status === 'Aktywna' ? Palette.green.green200 : Palette.gray.gray100 }}
                                sx={{
                                    borderRadius: '6px',
                                }}
                            />
                        )}
                    />
                    <FunctionField label={translate('common.priorityLabel')} source='priority' render={record => <MarketingPriorityField record={record} />} />
                    <FunctionField
                        label={translate('resources.campaign.labels.views')}
                        source='views'
                        render={record => <MarketingBarIndicatorField record={record} isViewsField={true} />}
                    />
                    <FunctionField
                        label={translate('resources.campaign.labels.conversion')}
                        source='conversion'
                        render={record => <MarketingBarIndicatorField record={record} />}
                    />
                    <FunctionField
                        label={translate('resources.campaign.labels.options')}
                        onClick={e => e.stopPropagation()}
                        render={record => <PromotionalCampaingsMoreActionsPopover record={record} />}
                    />
                </CustomDatagridConfigurable>
            </>
        </CustomList>
    );
};
